import { RiInformationLine, RiSlackLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { Offcanvas } from "react-bootstrap";
import { AiOutlineSlack } from "react-icons/ai";
import { BiHelpCircle } from "react-icons/bi";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { MdOutlineHelpCenter } from "react-icons/md";
import {
    Article,
    GetSupportSlackConnectChannelDocument,
    Permission,
    useGetArticleQuery,
    useGetSupportSlackConnectChannelQuery,
    useSendSupportSlackConnectMutation,
} from "../../__generated__/api";
import { HelpTypes, useOnHelpEvent } from "../../contexts/HelpContext";
import useLogging, { LoggingHandler } from "../../contexts/logging/LoggingContext";
import useCurrentSession from "../../contexts/session";
import { useTheme } from "../../contexts/Theme";
import ArticleView from "../../pages/answers/details-v3/Article";
import Button, { VariantTypes as ButtonVariant } from "../theme/button/Button";
import Link from "../theme/button/Link";
import Styles from "./Help.module.scss";

export const ARTICLES = {

    HOME: "urn:fq:article:3a4f559ab4cc4143a650bdb4cc42e304",

    CONTACTED_PAGE: "urn:fq:article:791a4edd733a4f368e5a7eeca23aaa83",
    CONTACT_EXPLAIN_THREADED: "urn:fq:article:746f6f02aabf4edb85a36079d42ce87b",
    CONTACT_EXPLAIN_EMOJI: "urn:fq:article:c411476bdb014907ab59689ad86da49d",
    CONTACT_EXPLAIN_CHANNEL_MENU_EXTERNAL: "urn:fq:article:c5f541eef50a4133be243b016e1496b5",
    CONTACT_EXPLAIN_CHANNEL_MENU_INTERNAL: "urn:fq:article:fb81b0f04f634b77ba84a1e3d5ae0125",
    ONBOARDING_COMMON_TASKS: "urn:fq:article:63b85aaae0b649f2a2699cee7a119595",
    ONBOARDING_ENABLE_AI: "urn:fq:article:6713713e10104e3c930f43a631b0ef14",

    PICK_ROLE_PAGE: "urn:fq:article:98b45b9a21bf40fb94d94f6dc086635c",

    CUSTOMIZE_PAGE: "urn:fq:article:2bfd929f753a462da2b9c7048941a578",
    ONBOARDING_DONE: "urn:fq:article:723cc574bf62476f95755689ffdbd93a",

    TICKETING_SYSTEM_PAGE_EXTERNAL: "urn:fq:article:e9066342e6a842bca7e6ab7fa67930e6",
    TICKETING_SYSTEM_PAGE_INTERNAL: "urn:fq:article:d48998a13aa048ec895c5721a819ae72",

    ONBOARDING_HOME_PAGE: "urn:fq:article:a3813fa56aaf45479f7c8b72642e34d5",
    HOME_PAGE: "urn:fq:article:b31cab70032943ce87b192f19334a67a",

    SETTINGS_HOME: "urn:fq:article:3168d37004cc438b9d926cdd7cd01678",

    ACTIONS_FEEDBACK: "urn:fq:article:9579628666eb40a7ab5fa1fcaf5df4fc",
    ACTIONS_DEFLECTION: "urn:fq:article:9579628666eb40a7ab5fa1fcaf5df4fc",

    HOME_PAGE_V3: "urn:fq:article:84d73b0beeac46ceb39ef7b1068d028d",

    ONBOARDING_TEAMS_INSTALLED_VENDOR: "urn:fq:article:8d54b1d31aec45b2b39fcbc13a8e8350",

    WEB_CHAT_USER_AUTHENTICATION: "urn:fq:article:98d19296277648888ca71b6195984052",

    REPORT_ADD_WIDGET: "urn:fq:article:2fbdcc1230c74e44bcebaf6408f4c2cc",
    REPORTS: "urn:fq:article:52b2589c906d472a85c7509070b35046",

    ACCOUNT_DETAILS: "urn:fq:article:c4db19414bc6432f9aae5f2d78740824",

    SETTINGS_INSTLLING_HOW_TO: "urn:fq:article:5aea78e059584b609c9dd352d8b31aa8",

    AUTOMATION_CREATE: "urn:fq:article:14ea229ef3a840e19753c5d9047ad835",

};

export interface Props {
    children?: ReactNode;
    button?: ReactNode;
    buttonType?: "icon" | "help" | "small-icon",
    buttonClassName?: string;
    buttonVariant?: ButtonVariant;
    buttonClicked?: () => void;
    articleId?: string
    title?: string;
    metricPathPrefix?: string[];
}

interface HistoryItem {
    id: string;
    title: string;
}

export default function Help(props: Props) {
    const theme = useTheme();
    const logging = useLogging();
    const [articleId, setArticleId] = useState<string | undefined>(props.articleId);
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const { permissions } = useCurrentSession();

    const [isExpanded, setIsExpanded] = useState(false);
    const canHaveChannel = permissions.has(Permission.Administrator) || permissions.has(Permission.ChatsModifyAll);
    const { data: supportChannel } = useGetSupportSlackConnectChannelQuery({
        skip: !canHaveChannel || !isExpanded,
    });
    const { data: article, loading: isArticleLoading } = useGetArticleQuery({
        skip: !articleId || !isExpanded,
        variables: {
            id: articleId,
        },
    });

    useOnHelpEvent(HelpTypes.HELP, () => setIsExpanded(true));

    const [invite] = useSendSupportSlackConnectMutation();

    const sendInvite = async () => {
        await invite({
            refetchQueries: [GetSupportSlackConnectChannelDocument],
        });
    };

    useEffect(() => {
        if (isExpanded) {
            setArticleId(props.articleId);
            setHistory([]);
            logging.debug({ articleId, title: props.title }, "User opened help");
        }
    }, [isExpanded]);

    let button: ReactElement;

    const openClicked = () => {
        if (props.metricPathPrefix) {
            LoggingHandler.instance.metric([...props.metricPathPrefix, "show"]);
        }

        setIsExpanded(!isExpanded);
        props.buttonClicked?.();
    };
    if (props.buttonType === "small-icon") {
        button = (
            <Button
                type="button"
                className={classNames(Styles["small-icon"], props.buttonClassName)}
                onClick={openClicked}
                prefixIcon={<RiInformationLine />}
                title="Open the help center"
                variant={props.buttonVariant}
                size="small"
            >
                {props.button}
            </Button>
        );
    } else if (props.buttonType === "help") {
        button = (
            <button
                type="button"
                className={props.buttonClassName}
                onClick={openClicked}
                title="Open the help center"
            >
                <BiHelpCircle />
                {" "}
                Help
            </button>
        );
    } else if (props.button) {
        button = (
            <button
                type="button"
                className={props.buttonClassName}
                onClick={openClicked}
                title="Open the help center"
            >
                {props.button}
            </button>
        );
    } else {
        button = (
            <div
                className={"position-absolute top-50 end-0 translate-middle-y bg-default "
        + "rounded-start p-1 d-flex flex-column"}
            >
                <button
                    type="button"
                    className="btn btn-none p-1"
                    onClick={openClicked}
                    aria-label="View Help center"
                    title="Open the help center"
                >
                    <MdOutlineHelpCenter size="25px" className="text-light" />
                </button>
            </div>
        );
    }

    return (
        <>
            {button}

            <Offcanvas show={isExpanded} onHide={() => setIsExpanded(false)} placement="end">
                <Offcanvas.Header closeButton className="help">
                    <Offcanvas.Title>{props.title || article?.article?.title || "Help"}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className={`d-flex flex-column w-h50 ${Styles["help-body"]}`}>
                    {history.isNotEmpty && (
                        <nav aria-label="breadcrumb">
                            <div className="breadcrumb text-light">
                                {history.last(3).map((item, index) => (
                                    <button
                                        className={`btn btn-none text-light p-0 breadcrumb-item pe-2 ${Styles.breadcrumb}`}
                                        aria-current="page"
                                        type="button"
                                        key={item.id}
                                        onClick={() => {
                                            setArticleId(item.id);
                                            const copy = [...history];
                                            copy.splice(index);
                                            setHistory(copy);
                                        }}
                                    >
                                        {item.title}
                                    </button>
                                ))}
                            </div>
                        </nav>

                    )}
                    <div className="flex-grow-1 overflow-auto mb-3 border rounded p-2 position-relative">
                        {props.children}
                        {articleId && (
                            <ArticleView
                                article={article?.article as Article}
                                isLoading={isArticleLoading}
                                isReadOnly
                                shouldShowHeader={false}
                            />

                        )}

                    </div>

                    {canHaveChannel && (supportChannel?.supportSlackConnectChannel?.name ? (
                        <p className="text-wrap border rounded p-2 px-2">

                            {supportChannel?.supportSlackConnectChannel.isAccepted ? (
                                <div>
                                    <strong
                                        className="fw-medium d-flex w-100 justify-content-between"
                                        title="Seems like you already have a Slack Connect channel with
                                            Foqal support. If you can not access it please reach out to your Slack Administrator."
                                    >
                                        <span>
                                            Want to Chat?
                                        </span>
                                        <span>
                                            <IoIosHelpCircleOutline />
                                        </span>
                                    </strong>
                                    {theme.isV3 ? (
                                        <Link
                                            className="w-100"
                                            variant="outline-blue"
                                            href={supportChannel?.supportSlackConnectChannel?.url as string}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={props.metricPathPrefix
                                                ? LoggingHandler.instance.wrapMetric([...props.metricPathPrefix, "slack-connect"])
                                                : undefined}
                                        >
                                            <RiSlackLine />
                                            {" "}
                                            Slack with Support
                                        </Link>
                                    ) : (
                                        <a
                                            href={supportChannel?.supportSlackConnectChannel?.url as string}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-default border rounded py-2 w-100 text-gray-100 d-block mt-2"
                                        >
                                            <AiOutlineSlack />
                                            {" "}
                                            Slack with Support
                                        </a>
                                    )}

                                </div>
                            ) : (
                                <div>

                                    <strong className="fw-medium d-block">
                                        <AiOutlineSlack />
                                        {" "}
                                        Want to chat?
                                    </strong>
                                    <small>
                                        We have sent a Slack Connect Channel invite to your email. Once you accept that
                                        its the easiest place to get ahold of us for any questions you may have. If you
                                        do not see this email, talk to your Slack administrator or contact love@foqal.io
                                        for help.
                                    </small>
                                </div>
                            )}
                        </p>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-default border rounded py-3 w-100 text-gray-100 d-block"
                            onClick={props.metricPathPrefix
                                ? LoggingHandler.instance.wrapMetric([
                                    ...props.metricPathPrefix,
                                    "slack-connect-create",
                                ], sendInvite)
                                : sendInvite}
                            title="Get help from the help center"
                        >
                            <h4 className="text-gray-100">
                                <AiOutlineSlack />
                                {" "}
                                Slack Connect
                            </h4>
                            <p className="text-wrap mb-0">
                                Click here for a Slack Connect invite sent to your email. It is the best place to get
                                help and support, and see a demo of Foqal.
                            </p>
                        </button>

                    ))}

                    <a
                        type="button"
                        className="btn border rounded py-3 w-100 d-block mt-2"
                        href="https://support.foqal.io"
                        target="_blank"
                        rel="noreferrer"
                        title="Get help from the help center"
                        onClick={props.metricPathPrefix
                            ? LoggingHandler.instance.wrapMetric([...props.metricPathPrefix, "help-center"])
                            : undefined}
                    >
                        <h5 className="my-0">
                            <BiHelpCircle />
                            {" "}
                            Help Center
                        </h5>

                    </a>

                </Offcanvas.Body>
            </Offcanvas>

        </>
    );
}

Help.defaultProps = {
    title: "",
    buttonType: "icon",
    children: undefined,
    articleId: undefined,
    button: undefined,
    buttonClassName: "",
    buttonClicked: undefined,
    buttonVariant: "none",
    metricPathPrefix: undefined,
};
